<script setup lang="ts">
import type { TableOptions } from '@core/stores/user'
import type { SortItem } from '@core/types'
import { useReportFilters } from '@/composables/useReportFilters'
import ReportFiltersBar from '@/views/pages/reports/ReportFiltersBar.vue'

definePage({
  meta: {
    action: 'read',
    subject: 'analytics',
  },
})

const { t } = useI18n()
const table = 'users-ranking'

const headers = [
  { title: t(`${table}-page.user`), key: 'user' },
  { title: t(`${table}-page.effectiveness`), key: 'effectiveness' },
  { title: t(`${table}-page.average-effectiveness`), key: 'average_effectiveness' },
  { title: t(`${table}-page.sessions-count`), key: 'evaluated_games_count', sortable: false },
  { title: t(`${table}-page.department`), key: 'department' },
  { title: t(`${table}-page.position`), key: 'position' },
]

const defaultTableOptions: TableOptions = {
  sortBy: 'effectiveness',
  orderBy: 'desc',
  page: 1,
  perPage: 10,
}

const cookieTableOptions = useCookie<TableOptions>(`${table}TableOptions`, {
  default: () => defaultTableOptions,
})

let currentTableOptions: TableOptions | undefined = cookieTableOptions.value

if (currentTableOptions === undefined) {
  currentTableOptions = defaultTableOptions

  const newTableOptions: any = cookieTableOptions.value

  newTableOptions.push(currentTableOptions)

  useCookie(`${table}TableOptions`).value = newTableOptions
}

const searchQuery = ref('')
const debouncedSearch = useDebounce(searchQuery, 300)
const itemsPerPage = ref(currentTableOptions.perPage)
const page = ref(currentTableOptions.page)
const sortBy = ref<string>(currentTableOptions.sortBy)
const orderBy = ref<undefined | boolean | 'asc' | 'desc'>(currentTableOptions.orderBy)
const currenItem = ref()
const isLoading = ref(false)
const isAddDialogOpen = ref(false)
const isDeleteConfirmDialogVisible = ref(false)

const updateOptions = (options: any) => {
  page.value = options.page
  if (options.sortBy[0] !== undefined) {
    sortBy.value = options.sortBy[0]?.key
    orderBy.value = options.sortBy[0]?.order
  }
  else {
    sortBy.value = defaultTableOptions.sortBy
    orderBy.value = defaultTableOptions.orderBy
  }
}

const sortTable: SortItem[] = [{ key: sortBy.value, order: orderBy.value }]

const reportFilters = useReportFilters({
  fields: ['competence', 'departments', 'positions', 'employees', 'scenarios'],
  mode: 'ranking',
})

// Sync, so the table refetches once with page 1 instead of first with the old page.
watch(reportFilters.filters, () => {
  page.value = 1
}, { flush: 'sync' })

const { data: itemsData } = await useApi<any>(createUrl('/statistics/users-ranking-by-competence/',
  {
    query: () => ({
      search: debouncedSearch,
      page,
      itemsPerPage,
      sortBy,
      orderBy,
      ...reportFilters.query.value,
    }),
  },
))

interface Item {
  id: string
  title: string
}
const { data: departmentsList } = await useApi<any>('/departments/all')

let departments: Item[] = [{ title: '', id: '1' }]
if (departmentsList?.value)
  departments = departmentsList.value

const { data: positionsList } = await useApi<any>('/positions/all')

let positions: Item[] = [{ title: '', id: '1' }]
if (positionsList?.value)
  positions = positionsList.value

function findTitleById(items: Item[], id: string): string | undefined {
  const item: any = items.find((i: any) => i.id === id)

  return item?.title
}

const getExportUrl = () => {
  return createUrl('/api/statistics/users-ranking-by-competence/export',
    {
      query: () => ({
        search: debouncedSearch,
        page,
        itemsPerPage,
        sortBy,
        orderBy,
        ...reportFilters.query.value,
      }),
    },
  ).value
}

const items = computed(() => itemsData.value?.data)

const total = computed(() => itemsData.value?.total)

watch([
  isDeleteConfirmDialogVisible, isAddDialogOpen,
], (val, old) => {
  if ((val[0] === false && old[0] === true) || (val[1] === false && old[1] === true))
    setTimeout(() => currenItem.value = undefined, 200)
})

watch([page, itemsPerPage, sortBy, orderBy], (val, old) => {
  if (val[1] !== old[1])
    page.value = 1

  useCookie(`${table}TableOptions`).value = JSON.stringify({
    page: val[0],
    perPage: val[1],
    sortBy: val[2] === undefined ? currentTableOptions?.sortBy : val[2],
    orderBy: val[3] === undefined ? currentTableOptions?.orderBy : val[3],
  })
})
</script>

<template>
  <div class="d-flex flex-column gap-6">
    <ReportFiltersBar :report-filters="reportFilters" />
    <VCard>
      <VCardText class="d-flex flex-wrap py-4 gap-4">
        <div class="me-3 d-flex gap-3">
          <VTextField
            v-model="searchQuery"
            clearable
            :placeholder="t('search')"
            style=" min-inline-size: 200px;"
          />
        </div>
        <VSpacer />
        <div class="d-flex align-center flex-wrap gap-4">
          <VBtn
            variant="tonal"
            color="secondary"
            prepend-icon="tabler-upload"
            :href="getExportUrl()"
            target="_blank"
            rel="noopener noreferrer"
          >
            {{ t(`${table}-page.export`) }}
          </VBtn>
        </div>
      </VCardText>
      <div class="category-table">
        <VDataTableServer
          v-model:items-per-page="itemsPerPage"
          v-model:page="page"
          :headers="headers"
          :sort-by="sortTable"
          :loading="isLoading"
          :loading-text="t('loading')"
          :items="items"
          :items-length="total"
          class="text-no-wrap"
          @update:options="updateOptions"
        >
          <template #item.user="{ item }">
            <div class="d-flex align-center">
              <VAvatar
                size="34"
                :variant="!item.profile.has_avatar ? 'tonal' : undefined"
                :color="undefined"
                class="me-3"
              >
                <VImg
                  v-if="item.profile.has_avatar"
                  :src="`/api/account/avatar-${item.id}`"
                />
                <span v-else>{{ avatarText(item.profile.name + item.profile.last_name) }}</span>
              </VAvatar>
              <div class="d-flex flex-column">
                <h6 class="text-base">
                  <RouterLink
                    :to="{ name: 'users-view-id', params: { id: item.id } }"
                    class="font-weight-medium text-link"
                  >
                    {{ `${item.profile.name} ${item.profile.last_name}` }}
                  </RouterLink>
                </h6>
                <span class="text-sm text-medium-emphasis">{{ item.email }}</span>
              </div>
            </div>
          </template>
          <template #item.effectiveness="{ item }">
            {{ item.profile.effectiveness ?? item.effectiveness }}
          </template>
          <template #item.average_effectiveness="{ item }">
            {{ item.average_effectiveness !== null ? `${item.average_effectiveness}%` : '—' }}
          </template>
          <template #item.evaluated_games_count="{ item }">
            {{ item.evaluated_games_count }}
          </template>
          <template #item.department="{ item }">
            {{ findTitleById(departments, item.profile.department_id) }}
          </template>
          <template #item.position="{ item }">
            {{ findTitleById(positions, item.profile.position_id) }}
          </template>
          <template #item.title="{ item }">
            {{ item.title }}
          </template>
          <template #bottom>
            <div>
              <VDivider />

              <div class="d-flex align-center justify-sm-space-between justify-center flex-wrap gap-3 px-6 py-3">
                <AppSelect
                  v-model="itemsPerPage"
                  density="compact"
                  class="app-select flex-grow-0"
                  :items="[5, 10, 25, 50]"
                  style="max-inline-size: 5rem;min-inline-size: 5rem;"
                />

                <VPagination
                  v-model="page"
                  active-color="primary"
                  :length="Math.ceil(total / itemsPerPage)"
                  :total-visible="$vuetify.display.xs ? 1 : Math.min(Math.ceil(total / itemsPerPage), 5)"
                />
              </div>
            </div>
          </template>
        </VDataTableServer>
      </div>
    </VCard>
  </div>
</template>
